import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledDateField, ControlledSelectField, ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { WorkOrderChangeStatusProps } from 'constantsProject';
import type { ProjectData, WorkOrderChangeData } from 'gql/graphql';
import { ContractStatusEnum, ProjectStatusEnum, WorkOrderChangeStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { cnMixSpace, Grid, GridItem } from 'uikit';
import { formatDateToLocale, newDate } from 'utils';
import { requiredContractChangeApprovedRule } from 'utils/constants';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    useCreateWorkOrderChange,
    useGetStatusWarningText,
    useIsUniqueWorkOrderChangeInternalNumber,
    useUpdateWorkOrderChange,
} from '../hooks';

export const WorkOrderChangeRequisites = ({
    onCancel,
    isCreating,
    projectData,
    workOrderLabel,
    workOrderId,
    contractId,
    refreshProjectData,
    refreshWorkOrderChangeData,
    workOrderChangeData,
    statusContract,
    statusProject,
}: {
    onCancel: () => void;
    isCreating: boolean;
    projectData: ProjectData | undefined | null;
    workOrderLabel: string;
    workOrderId: number;
    contractId: number;
    statusContract?: ContractStatusEnum;
    statusProject?: ProjectStatusEnum;
    refreshProjectData?: () => void;
    refreshWorkOrderChangeData?: () => void;
    workOrderChangeData?: Omit<WorkOrderChangeData, 'workOrderId' | 'contractId'> | undefined | null;
}) => {
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();
    const { workOrderChangeUer: workOrderChangeUerRules } = useAccess();

    const formRef = useRef<HTMLFormElement>(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id: projectId, orderChangeId, methodology } = useProjectParams();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isWarningDialogOpened, setIsWarningDialogOpened] = useState<boolean>(false);
    const [isStatusDialogOpened, setIsStatusDialogOpened] = useState<boolean>(false);

    const checkedWorkOrderNavs = useMemo(
        () => (projectData as any)?.workOrderNavs?.find((workOrder: any) => workOrder.id === workOrderId),
        [projectData, workOrderId],
    );

    const isDisabledByWorkOrderStatus = checkedWorkOrderNavs?.status === WorkOrderStatusEnum.Delayed;
    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;
    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const { control, handleSubmit, reset, setError, clearErrors, formState, resetField } = useForm({
        criteriaMode: 'all',
        shouldUnregister: true,
    });

    const internalNumber = useWatch({
        control,
        name: 'internalNumber',
    });

    const isInternalNumberUnique = useIsUniqueWorkOrderChangeInternalNumber({
        workOrderChangeId: Number(orderChangeId),
        workOrderId,
        newInternalNumber: internalNumber,
        isCreating,
    });

    useEffect(() => {
        if (internalNumber && workOrderChangeData?.internalNumber !== internalNumber && !isInternalNumberUnique) {
            setError('internalNumber', {
                message: 'Изм с таким номером уже существует. Введите другой номер',
            });
        } else {
            clearErrors('internalNumber');
        }
    }, [clearErrors, internalNumber, isInternalNumberUnique, setError, workOrderChangeData?.internalNumber]);

    const { creatingData, creatingStatus, creatingErr, creatingRequest, resetCreatingData, isCreatingInProgress } =
        useCreateWorkOrderChange();

    const { updatingData, updatingStatus, updatingErr, updatingRequest, resetUpdateData, isUpdatingInProgress } =
        useUpdateWorkOrderChange();

    const statusItems = useMemo(() => {
        const followers = (workOrderChangeData?.statusFollowers || []).map((v) => WorkOrderChangeStatusProps[v]);

        return [
            ...followers,
            ...(workOrderChangeData?.status ? [WorkOrderChangeStatusProps[workOrderChangeData?.status]] : []),
        ];
    }, [workOrderChangeData?.status, workOrderChangeData?.statusFollowers]);

    const isCommitting = useMemo(
        () => isCreatingInProgress || isUpdatingInProgress,
        [isCreatingInProgress, isUpdatingInProgress],
    );

    const requestData = useMemo(
        () => ({
            data: creatingData || updatingData,
            error: creatingErr || updatingErr,
            status: creatingStatus || updatingStatus,
            result: creatingData?.result || updatingData?.result,
            validation: creatingData?.validation || updatingData?.validation,
        }),
        [creatingData, creatingErr, creatingStatus, updatingData, updatingErr, updatingStatus],
    );

    const analysisData = useMemo(
        () => ({
            projectId,
            contractId: projectData?.contractId,
            orderId: workOrderId,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        }),
        [
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            workOrderId,
        ],
    );

    const submitHandler = useCallback(
        (data) => {
            const variables = {
                contractId,
                workOrderId,
                status: data?.status?.id,
                internalNumber: data?.internalNumber,
                signingDate: data?.signingDate,
                description: data?.description,
            };

            if (isCreating) {
                dispatch(
                    creatingRequest({
                        input: variables,
                    }),
                );
            } else {
                const numberOrderChangeId = workOrderChangeData?.id ?? Number(orderChangeId);

                dispatch(
                    updatingRequest({
                        workOrderChangeInput: { id: numberOrderChangeId, ...variables },
                    }),
                );

                sendGpnAnalyticsEvent({
                    componentId: 'rim_save IZM button_pressed',
                    ...analysisData,
                    orderChangeStatus: data?.status?.id,
                    orderChangeId: numberOrderChangeId,
                });
            }

            setIsSubmitted(true);
        },
        [
            analysisData,
            contractId,
            creatingRequest,
            dispatch,
            isCreating,
            orderChangeId,
            sendGpnAnalyticsEvent,
            updatingRequest,
            workOrderChangeData?.id,
            workOrderId,
        ],
    );

    const handleSave = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, [formRef]);

    useEffect(() => {
        const defaultStatus = WorkOrderChangeStatusProps[WorkOrderChangeStatusEnum.OnFormation];
        const requisitesFormData = {
            subject: projectData?.name,
            workOrderName: workOrderLabel,
            author: projectData?.authoredBy,
            internalNumber: workOrderChangeData?.internalNumber,
            updatedAt: formatDateToLocale(workOrderChangeData?.updatedAt),
            signingDate: newDate(workOrderChangeData?.signingDate),
            description: workOrderChangeData?.description,
            status: workOrderChangeData?.status
                ? WorkOrderChangeStatusProps[workOrderChangeData?.status]
                : defaultStatus,
        };

        reset(requisitesFormData);
    }, [
        projectData?.authoredBy,
        projectData?.name,
        reset,
        workOrderChangeData?.description,
        workOrderChangeData?.internalNumber,
        workOrderChangeData?.signingDate,
        workOrderChangeData?.status,
        workOrderChangeData?.updatedAt,
        workOrderLabel,
    ]);

    const resetAndCloseHandler = useCallback(() => {
        resetCreatingData(null);
        resetUpdateData(null);
        onCancel();
    }, [onCancel, resetCreatingData, resetUpdateData]);

    const closeRequisitesModal = useCallback(() => {
        if (!formState.isDirty && !isSubmitted) {
            resetAndCloseHandler();
        } else {
            setIsWarningDialogOpened(true);
        }
    }, [formState.isDirty, isSubmitted, resetAndCloseHandler]);

    useEffect(() => {
        if (isSubmitted && requestData?.result) {
            const newId = requestData?.data?.data?.id;

            if (isCreating && newId) {
                navigate(`/projects/${methodology}/${projectId}/orderChange/${newId}`);

                sendGpnAnalyticsEvent({
                    componentId: 'rim_IZM file_saved',
                    ...analysisData,
                    orderChangeStatus: WorkOrderChangeStatusEnum.OnFormation,
                    orderChangeId: newId,
                });
            } else {
                refreshWorkOrderChangeData?.();
            }
            refreshProjectData?.();
            resetAndCloseHandler();
        }
    }, [
        analysisData,
        isCreating,
        isSubmitted,
        methodology,
        navigate,
        projectId,
        refreshProjectData,
        refreshWorkOrderChangeData,
        requestData?.data?.data?.id,
        requestData?.result,
        resetAndCloseHandler,
        sendGpnAnalyticsEvent,
    ]);

    const onSaveErrorReport = useCallback(() => {
        ExportCSVReport(requestData?.validation);
    }, [requestData?.validation]);

    const onCloseSaveErrorModal = useCallback(() => {
        resetAndCloseHandler();
        setIsSubmitted(false);
    }, [resetAndCloseHandler]);

    const closeWarningDialogHandler = useCallback(() => {
        resetAndCloseHandler();
    }, [resetAndCloseHandler]);

    const cancelWarningDialogHandler = useCallback(() => {
        setIsWarningDialogOpened(false);
    }, []);

    useEffect(() => {
        if (formState.dirtyFields.status) {
            setIsStatusDialogOpened(true);
        }
    }, [formState.dirtyFields.status]);

    const cancelStatusChangeHandler = useCallback(() => {
        resetField('status');
        setIsStatusDialogOpened(false);
    }, [resetField]);

    const status = useWatch({ control, name: 'status' });

    const statusDialogWarningText = useGetStatusWarningText({
        currentStatus: workOrderChangeData?.status,
        nextStatus: status?.id,
    });

    const isNextStatusApproved = useMemo(() => status?.id === WorkOrderChangeStatusEnum.Approved, [status?.id]);

    const isDisabledActions =
        workOrderChangeData?.status === WorkOrderChangeStatusEnum.Approved ||
        isDisabledByProjectStatus ||
        isDisabledByWorkOrderStatus ||
        !isContractStatusWork;

    const submitStatusChangeHandler = useCallback(() => {
        setIsStatusDialogOpened(false);

        sendGpnAnalyticsEvent({
            componentId: 'rim_change IZM button_pressed',
            ...analysisData,
            orderChangeStatus: status?.id,
        });
    }, [analysisData, sendGpnAnalyticsEvent, status?.id]);

    const isChangeDataStatusApproved = useMemo(
        () => workOrderChangeData?.status === WorkOrderChangeStatusEnum.Approved,
        [workOrderChangeData?.status],
    );

    const onConfirm = useMemo(
        () => (isChangeDataStatusApproved ? undefined : handleSave),
        [handleSave, isChangeDataStatusApproved],
    );

    const btnCancelLabel = useMemo(
        () => (isChangeDataStatusApproved ? 'Закрыть' : 'Отменить'),
        [isChangeDataStatusApproved],
    );

    const btnCancelView = useMemo(
        () => (isChangeDataStatusApproved ? 'primary' : 'ghost'),
        [isChangeDataStatusApproved],
    );

    const isStatusDisabled = useMemo(
        () =>
            isCreating ||
            isDisabledActions ||
            (!workOrderChangeUerRules?.canEditCompletedStatus &&
                workOrderChangeData?.status === WorkOrderChangeStatusEnum.Approved),
        [isCreating, isDisabledActions, workOrderChangeData?.status, workOrderChangeUerRules?.canEditCompletedStatus],
    );

    return (
        <ModalDialog
            title="Данные Изм. к нз"
            isOpen
            btnCancelLabel={btnCancelLabel}
            onConfirm={onConfirm}
            onClose={closeRequisitesModal}
            isCommit={isCommitting}
            withTitleCloseButton
            btnConfirmDisabled={isDisabledActions}
            btnCancelView={btnCancelView}
        >
            <ModalDialog
                title="Изменение статуса"
                isOpen={isStatusDialogOpened}
                onConfirm={submitStatusChangeHandler}
                onClose={cancelStatusChangeHandler}
                btnConfirmLabel="Изменить"
                btnCancelLabel="Не изменять"
                withTitleCloseButton
                style={{ width: 520 }}
                bodyMinHeight="minContent"
            >
                <div style={{ padding: 20, lineHeight: '24px' }}>{statusDialogWarningText}</div>
            </ModalDialog>
            <form className="" onSubmit={handleSubmit(submitHandler)} ref={formRef}>
                <Grid cols={1} style={{ width: 600, padding: '20px 20px 8px 20px' }}>
                    <GridItem>
                        <ControlledSelectField
                            control={control}
                            label="Статус"
                            name="status"
                            items={statusItems}
                            className={cnMixSpace({ mB: 's' })}
                            disabled={isStatusDisabled}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Предмет договора"
                            name="subject"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Наряд-Заказ"
                            name="workOrderName"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Номер Изм. к НЗ"
                            name="internalNumber"
                            className={cnMixSpace({ mB: 's' })}
                            disabled={isDisabledActions}
                            rules={isNextStatusApproved ? requiredContractChangeApprovedRule : undefined}
                            required={isNextStatusApproved}
                            key={`internalNumber_${isNextStatusApproved}`}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Автор"
                            name="author"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Дата изменения"
                            name="updatedAt"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                            placeholder="ДД.ММ.ГГГГ"
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledDateField
                            control={control}
                            label="Дата подписания"
                            name="signingDate"
                            type="date"
                            className={cnMixSpace({ mB: 'l' })}
                            disabled={isDisabledActions}
                            rules={isNextStatusApproved ? requiredContractChangeApprovedRule : undefined}
                            required={isNextStatusApproved}
                            key={`signingDate_${isNextStatusApproved}`}
                            withClearButton={!isChangeDataStatusApproved}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Комментарий"
                            name="description"
                            className={cnMixSpace({ mB: 's' })}
                            disabled={isDisabledActions}
                        />
                    </GridItem>
                </Grid>
            </form>
            <ErrorHandlingModal
                titleText="Реквизиты Изм. к нз"
                isSubmitted={isSubmitted}
                statusRequest={requestData.status}
                dataRequest={requestData.data}
                errorsRequest={requestData.error}
                handleCloseModalError={onCloseSaveErrorModal}
                handleCreateReport={onSaveErrorReport}
            />

            <ConfirmationDialog
                textHeader=""
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                isOpened={isWarningDialogOpened}
                onSubmit={closeWarningDialogHandler}
                onClose={cancelWarningDialogHandler}
            />
        </ModalDialog>
    );
};

WorkOrderChangeRequisites.displayName = 'WorkOrderChangeRequisites';
